// src/api/files.js
/**
 * File Handling API Layer
 * 
 * Manages file-related operations including:
 * - S3 presigned URL generation
 * - Direct S3 uploads with progress tracking
 * - Backend file processing triggers
 */
import { 
    PRESIGNED_URL_API_URL, 
    FILE_PROCESSOR_API_URL 
} from '../config';
import { getAuthHeaders, handleAuthError } from './auth';

/**
 * Gets a presigned URL for uploading a file to S3.
 * WITH AUTHENTICATION: Includes JWT token in the request.
 * 
 * @param {string} fileType - The MIME type of the file.
 * @returns {Promise<object>} - Object containing uploadUrl and objectKey.
 */
export const getPresignedUrl = async (fileType) => {
    console.log('FILE API: Getting presigned URL with authentication...');
    
    try {
        const headers = await getAuthHeaders();
        
        const url = `${PRESIGNED_URL_API_URL}?fileType=${encodeURIComponent(fileType)}`;
        
        const response = await fetch(url, {
            headers
        });
        
        if (response.status === 401) {
            handleAuthError(response.status, 'presigned URL generation');
        }
        
        const responseText = await response.text();
        console.log('FILE API: Raw presigned URL response received');
        
        if (!response.ok) {
            console.error('FILE API: Error response:', responseText);
            throw new Error(`Presigned URL API responded with status: ${response.status}`);
        }
        
        let data;
        try {
            data = JSON.parse(responseText);
        } catch (parseError) {
            console.error('FILE API: Failed to parse response as JSON:', parseError);
            throw new Error('Invalid JSON response from presigned URL API');
        }
        
        // Lambda proxy response handling with validation
        if (data.statusCode && data.body) {
            console.log('FILE API: Detected Lambda proxy response format');
            
            // Validate statusCode in proxy response
            if (data.statusCode !== 200) {
                console.error('FILE API: Lambda proxy returned error status:', data.statusCode);
                throw new Error(`Lambda function returned error: ${data.statusCode}`);
            }
            
            try {
                const actualData = JSON.parse(data.body);
                console.log('FILE API: Successfully extracted presigned URL data');
                
                if (!actualData.uploadUrl || !actualData.objectKey) {
                    console.error('FILE API: Missing required fields in response:', actualData);
                    throw new Error('Missing uploadUrl or objectKey in response');
                }
                
                return actualData;
            } catch (bodyParseError) {
                console.error('FILE API: Failed to parse body as JSON:', bodyParseError);
                throw new Error('Invalid JSON in response body');
            }
        } else {
            // Direct response format
            if (!data.uploadUrl || !data.objectKey) {
                console.error('FILE API: Invalid response structure:', data);
                throw new Error('Missing uploadUrl or objectKey in response');
            }
            
            return data;
        }
        
    } catch (error) {
        console.error('FILE API: Failed to get presigned URL:', error);
        throw error;
    }
};

/**
 * Uploads a file directly to S3 using a presigned URL.
 * Note: This doesn't require authentication as it uses a presigned URL.
 * 
 * @param {string} presignedUrl - The S3 presigned URL.
 * @param {File} file - The file to upload.
 * @param {function} onProgress - Progress callback function.
 * @returns {Promise<void>}
 */
export const uploadToS3 = async (presignedUrl, file, onProgress) => {
    console.log('FILE API: Uploading file to S3...');
    
    if (!presignedUrl) {
        console.error('FILE API: No presigned URL provided');
        throw new Error('No presigned URL provided for upload');
    }
    
    if (!file || !(file instanceof File || file instanceof Blob)) {
        console.error('FILE API: Invalid file object provided');
        throw new Error('Invalid file object provided for upload');
    }
    
    console.log('FILE API: File details:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: new Date(file.lastModified).toISOString()
    });

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Timeout handling
        xhr.timeout = 300000; // 5 minutes timeout for large files
        
        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                const percentComplete = (e.loaded / e.total) * 100;
                console.log('FILE API: Upload progress:', percentComplete.toFixed(2) + '%');
                if (typeof onProgress === 'function') {
                    onProgress(percentComplete);
                }
            }
        });
        
        xhr.addEventListener('load', () => {
            console.log('FILE API: Upload complete, status:', xhr.status);
            // S3 returns 200 for successful PUT operations
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                console.error('FILE API: Upload failed:', xhr.responseText);
                reject(new Error(`Upload failed with status: ${xhr.status}`));
            }
        });
        
        xhr.addEventListener('error', (e) => {
            console.error('FILE API: Network error during upload:', e);
            reject(new Error('Upload failed - network error'));
        });
        
        xhr.addEventListener('abort', () => {
            console.error('FILE API: Upload aborted');
            reject(new Error('Upload cancelled'));
        });
        
        xhr.addEventListener('timeout', () => {
            console.error('FILE API: Upload timeout');
            reject(new Error('Upload timeout - file may be too large'));
        });

        xhr.open('PUT', presignedUrl);
        // Set proper content type for S3
        if (file.type) {
            xhr.setRequestHeader('Content-Type', file.type);
        }
        xhr.send(file);
    });
};

/**
 * Triggers backend processing for an uploaded file.
 * WITH AUTHENTICATION: Includes JWT token in the request.
 * 
 * @param {string} s3Key - The S3 object key.
 * @param {string} fileName - The original file name.
 * @param {object} options - Optional processing parameters.
 * @returns {Promise<object>} - The processed content and metadata.
 */
export const triggerFileProcessing = async (s3Key, fileName, options = {}) => {
    console.log('FILE API: Triggering file processing with authentication...');
    
    if (!s3Key) {
        console.error('FILE API: No s3Key provided for processing');
        throw new Error('S3 key is required for file processing');
    }
    
    if (!fileName) {
        console.error('FILE API: No fileName provided for processing');
        throw new Error('File name is required for file processing');
    }
    
    try {
        const headers = await getAuthHeaders();
        
        const payload = {
            s3_key: s3Key,
            file_name: fileName,
            ...options // Allow for additional processing options
        };
        
        console.log('FILE API: Processing request payload:', {
            ...payload,
            s3_key: payload.s3_key.substring(0, 20) + '...' // Log truncated key for security
        });
        
        const response = await fetch(FILE_PROCESSOR_API_URL, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
        });
        
        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'file processing');
        }
        
        if (response.status === 413) {
            console.error('FILE API: File too large - 413 response');
            throw new Error('File is too large to process');
        }
        
        if (response.status === 415) {
            console.error('FILE API: Unsupported file type - 415 response');
            throw new Error('File type is not supported for processing');
        }
        
        if (response.status === 422) {
            console.error('FILE API: Unprocessable entity - 422 response');
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || errorData.error || 'File could not be processed');
        }
        
        if (!response.ok) {
            throw new Error(`File processor API failed with status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('FILE API: File processing complete');
        return result;
        
    } catch (error) {
        console.error('FILE API: Failed to trigger file processing:', error);
        throw error;
    }
};

/**
 * Validates file before upload
 * @param {File} file - The file to validate
 * @param {object} constraints - Validation constraints
 * @returns {object} - Validation result with isValid and error message
 */
export const validateFile = (file, constraints = {}) => {
    const {
        maxSize = 100 * 1024 * 1024, // 100MB default
        allowedTypes = null,
        allowedExtensions = null
    } = constraints;
    
    console.log('FILE API: Validating file:', file.name);
    
    // Check file size
    if (file.size > maxSize) {
        const sizeMB = (maxSize / (1024 * 1024)).toFixed(0);
        return {
            isValid: false,
            error: `File size exceeds maximum of ${sizeMB}MB`
        };
    }
    
    // Check file type
    if (allowedTypes && !allowedTypes.includes(file.type)) {
        return {
            isValid: false,
            error: `File type ${file.type} is not allowed`
        };
    }
    
    // Check file extension
    if (allowedExtensions) {
        const extension = file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(extension)) {
            return {
                isValid: false,
                error: `File extension .${extension} is not allowed`
            };
        }
    }
    
    console.log('FILE API: File validation passed');
    return { isValid: true };
};